import {
  Button,
  Grid,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { createStyles, makeStyles } from '@mui/styles'
import type { Theme } from '@mui/material/styles'
import React, { Fragment } from 'react'
import ArrowUpward from '@mui/icons-material/ArrowUpward'
import ArrowDownward from '@mui/icons-material/ArrowDownward'

import {
  DataColumnSettings,
  DataGridSettings,
  SortField
} from '../../interfaces/DataGrid/DataGridSettings'

interface DataGridProps<T> {
  children: (item: T) => React.ReactNode
  dividers?: boolean
  items: T[]
  windowing?: boolean
  settings: DataGridSettings
  sorters?: SortField[] | undefined
  onSortChanged: (sorters: SortField[]) => void
  childComponent?: JSX.Element | JSX.Element[]
  displayHeader?: boolean
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      textAlign: 'center',
      color: theme.palette.text.secondary
    },
    headerLink: {
      height: '36px',
      '&:hover': {
        color: '#2196f3',
        cursor: 'pointer'
      }
    },
    title: {
      fontWeight: 'bold'
    },
    text: {
      padding: 0,
      margin: 0
    }
  })
)

export const DataGrid = <T extends Object>({
  children,
  items,
  settings,
  sorters,
  onSortChanged,
  childComponent,
  displayHeader = true
}: DataGridProps<T>) => {
  const classes = useStyles()
  const theme = useTheme()
  const matchesLG = useMediaQuery(theme.breakpoints.up('lg'))

  const directionIcon = (dataField: string): JSX.Element => {
    const sortedField = sorters?.find(
      (sortedField: SortField) => sortedField.dataField === dataField
    )
    return sortedField ? (
      sortedField.sortOrder === 'ASC' ? (
        <ArrowUpward />
      ) : (
        <ArrowDownward />
      )
    ) : (
      <Fragment />
    )
  }

  const handleOnHeaderClick = (column: DataColumnSettings) => {
    if (!column.allowSorting) return
    let newSorters = [...sorters!]
    let sortField = sorters?.find(
      (sortedField) => sortedField.dataField === column.dataField
    )
    if (sortField) {
      sortField.sortOrder = sortField.sortOrder === 'DESC' ? 'ASC' : 'DESC'
    } else {
      sortField = { dataField: column.dataField, sortOrder: 'ASC' }
      newSorters.push(sortField)
    }
    if (settings.sortingMode === 'single') {
      newSorters = [sortField]
    }
    onSortChanged(newSorters)
  }

  const headerElements: any = []

  settings.columns.forEach(function (column) {
    if (column.allowSorting || matchesLG) {
      headerElements.push(
        <Grid
          item
          key={`HDR-${column.dataField}`}
          xs={column.xs}
          sm={column.sm || column.xs}
          lg={column.lg || column.sm || column.xs}
        >
          {column.childComp ? (
            childComponent
          ) : (
            <Button
              className={`${classes.title} ${classes.text}`}
              style={{
                cursor: column.allowSorting ? 'pointer' : 'default',
                background: '#FFFFFF'
              }}
              variant='outlined'
              color={column.allowSorting ? 'primary' : 'default'}
              fullWidth={true}
              startIcon={directionIcon(column.dataField)}
              onClick={() => handleOnHeaderClick(column)}
            >
              {column.title}
            </Button>
          )}
        </Grid>
      )
    }
  })

  const elements = items.map((item, index) => (
    <div key={index} className='item'>
      {children(item)}
    </div>
  ))

  return (
    <React.Fragment>
      <Grid
        container
        direction='row'
        style={{ display: displayHeader ? 'flex' : 'none' }}
      >
        {headerElements}
      </Grid>
      {elements}
    </React.Fragment>
  )
}
